import React, { useEffect } from 'react';
import { DragHandle } from '@components/drag-handle';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';

import { ModalV2Props } from './modal-v2.types';
import { useDragToClose } from './use-drag-to-close';

// Headless UI's Dialog closes on Escape. When closeOnEscape is false we swallow the key in the
// capture phase, before its listener runs.
const usePreventEscapeClose = (isOpen: boolean, closeOnEscape: boolean): void => {
  useEffect(() => {
    if (closeOnEscape || !isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [closeOnEscape, isOpen]);
};

// Sticky header/footer are matched by component type; everything else is the scrollable body.
const partitionModalChildren = (children: React.ReactNode) => {
  const childArray = React.Children.toArray(children);
  const header = childArray.find((child) => React.isValidElement(child) && child.type === ModalStickyHeader);
  const footer = childArray.find((child) => React.isValidElement(child) && child.type === ModalStickyFooter);
  const hasStickyRegions = header !== undefined || footer !== undefined;
  const bodyChildren = hasStickyRegions
    ? childArray.filter((child) => child !== header && child !== footer)
    : childArray;
  return { header, footer, hasStickyRegions, bodyChildren };
};

// Panel sizing/scroll classes. Extracted so the per-variant width branches don't inflate the
// component's cognitive complexity. An explicit `maxWidthClass` overrides the variant presets.
const panelClassName = (args: {
  hasStickyRegions: boolean;
  variant: ModalV2Props['variant'];
  maxWidthClass?: string;
  borderRadiusClass?: string;
  contentClassName?: string;
}): string =>
  classNames(
    'bg-primary relative w-full',
    'h-auto max-h-[95dvh]',
    'sm:align-middle lg:my-8',
    'shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]',
    args.borderRadiusClass,
    args.hasStickyRegions ? 'flex flex-col overflow-hidden' : 'overflow-auto overscroll-contain scrollbar-xceed',
    {
      'sm:max-w-xl lg:max-w-211.5': args.variant === 'lg' && !args.maxWidthClass,
      'sm:max-w-xl lg:max-w-(--breakpoint-md)': args.variant === 'md' && !args.maxWidthClass,
      'sm:max-w-md': args.variant === 'sm' && !args.maxWidthClass,
      'sm:max-w-4xl lg:max-w-7xl': args.variant === 'xl' && !args.maxWidthClass,
      'max-w-full max-h-full h-full w-full rounded-none': args.variant === 'full' && !args.maxWidthClass,
      [`${args.maxWidthClass}`]: !!args.maxWidthClass,
    },
    args.contentClassName
  );

/**
 * Modal component using Headless UI v2.1+ API with smooth transitions
 *
 * Features:
 * - iOS-like spring animations with cubic-bezier curves
 * - Drag to close on mobile devices (ref-based 60fps)
 * - Multiple size variants (sm, md, lg, xl, full)
 * - Support for multiple simultaneous modals with proper z-index stacking
 * - Fully accessible with ARIA attributes
 * - Modern Headless UI v2.1+ Transition API
 *
 * @example
 * ```tsx
 * <ModalV2
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   variant="md"
 *   hasMobileHandle
 * >
 *   <div className="p-6">
 *     <h2>Modal Title</h2>
 *     <p>Modal content</p>
 *   </div>
 * </ModalV2>
 * ```
 */
export const ModalV2 = ({
  isOpen,
  onClose,
  children,
  variant = 'md',
  hasMobileHandle = true,
  enableDragToClose = true,
  maxWidthClass,
  borderRadiusClass = 'rounded-t-lg sm:rounded-lg',
  overlayClassName,
  contentClassName,
  surfaceClassName,
  centerOnMobile = false,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  enableMultipleModals = false,
  onAfterLeave,
  zIndex = 40,
  ariaLabel = 'modal',
}: ModalV2Props) => {
  // Drag to close hook
  const { panelRef, isClosing, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, initiateClose } =
    useDragToClose({ isOpen, onClose, enabled: enableDragToClose });

  usePreventEscapeClose(isOpen, closeOnEscape);

  const { header, footer, hasStickyRegions, bodyChildren } = partitionModalChildren(children);

  const dragHandle = hasMobileHandle && (
    <DragHandle
      className='sm:hidden shrink-0'
      onClose={() => initiateClose('drag')}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    />
  );

  return (
    <Transition show={isOpen && !isClosing} appear afterLeave={onAfterLeave}>
      <Dialog
        onClose={closeOnBackdropClick ? () => initiateClose('backdrop') : () => {}}
        className='relative'
        aria-label={ariaLabel}
        __demoMode={enableMultipleModals}
        style={{ zIndex }}>
        <TransitionChild
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className={classNames('fixed', surfaceClassName ?? 'inset-0', 'backdrop-overlay', overlayClassName)}
            aria-hidden='true'
            style={{ zIndex }}
          />
        </TransitionChild>

        <TransitionChild
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='translate-y-full'
          enterTo='translate-y-0'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='translate-y-0'
          leaveTo='translate-y-full'>
          <div
            className={classNames(
              'fixed flex',
              centerOnMobile ? 'items-center justify-center p-4 sm:p-6' : 'items-end sm:items-center sm:justify-center',
              variant !== 'full' && !centerOnMobile && 'sm:px-4',
              surfaceClassName ?? 'inset-0 w-screen'
            )}
            style={{ zIndex }}>
            <DialogPanel
              ref={panelRef}
              className={panelClassName({
                hasStickyRegions,
                variant,
                maxWidthClass,
                borderRadiusClass,
                contentClassName,
              })}>
              {dragHandle}
              {hasStickyRegions ? (
                <>
                  {header && <div className='shrink-0'>{header}</div>}
                  <div className='flex-1 min-h-0 overflow-auto overscroll-contain scrollbar-xceed'>{bodyChildren}</div>
                  {footer && <div className='shrink-0'>{footer}</div>}
                </>
              ) : (
                children
              )}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
